import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';

import { RetailRateRequestProofType } from '@classpass/protobuf-definitions/lib/web/models/partners/retail_rate_requests';

import Message from 'components/Message';

import { bem } from 'helpers/class-name';
import { httpClient } from 'helpers/http';
import { reportError } from 'helpers/error-logging';

import URLS from 'constants/urls';

import { messages } from '../ServicePricingSection.messages';

import ProofFileUploadItem from './ProofFileUploadItem';
import {
  PROOF_ITEM_STATUS,
  ProofUploadNetworkError,
  errorMessageFor,
  getPrefillFromLatestProof,
  putFileToPresignedUrl,
  sanitizeFilename,
  validateFile,
} from './proofFileUpload.helpers';

import './ProofFileUpload.scss';

const { block, element } = bem('proof-file-upload');

const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp';

let nextLocalId = 0;
const makeLocalId = () => {
  nextLocalId += 1;
  return `proof-${nextLocalId}`;
};

const fetchLatestProof = async venueId => {
  const response = await httpClient.get(URLS.manageServices.services.retailRateRequestProof(venueId), {
    ignoreForbiddenError: true,
    params: { latest_only: true },
  });
  return (response.data || [])[0] || null;
};

const ProofFileUpload = forwardRef(({ venueId, userId, prefillEnabled }, ref) => {
  const { setValue } = useFormContext();
  const inputRef = useRef(null);

  const [items, setItems] = useState([]);
  const [topLevelError, setTopLevelError] = useState(null);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  // collectProofs() awaits these so a service is never created with an
  // in-flight or failed upload.
  const pendingUploadPromisesRef = useRef(new Set());

  // Pre-fill the latest proof on mount (Add flow only): fetch → interpret → apply.
  useEffect(() => {
    if (!prefillEnabled || !venueId) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const prefill = getPrefillFromLatestProof(await fetchLatestProof(venueId));
        if (cancelled || !prefill) return;

        if (prefill.kind === 'url') {
          setValue('linkToPrice', prefill.url);
        } else if (prefill.kind === 'file') {
          setItems([{ id: makeLocalId(), ...prefill.item }]);
        }
      } catch (error) {
        reportError(error, { message: 'Failed to pre-fill latest proof', venueId });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [prefillEnabled, venueId, setValue]);

  const updateItemStatus = (id, patch) => {
    setItems(prev => prev.map(item => (item.id === id ? { ...item, ...patch } : item)));
  };

  const uploadOne = async (itemId, file) => {
    updateItemStatus(itemId, { status: PROOF_ITEM_STATUS.UPLOADING, errorMessage: null });

    try {
      const sanitized = sanitizeFilename(file.name);
      const presignResponse = await httpClient.post(
        URLS.manageServices.services.retailRateRequestProof(venueId),
        {
          filename: sanitized,
          venue_id: venueId,
        },
        { ignoreForbiddenError: true }
      );
      const { upload_url: uploadUrl, proof_id: proofId } = presignResponse.data || {};
      if (!uploadUrl || !proofId) {
        throw new Error('Pre-sign response missing upload_url or proof_id');
      }

      await putFileToPresignedUrl(uploadUrl, file);

      updateItemStatus(itemId, {
        status: PROOF_ITEM_STATUS.SUCCESS,
        fileProofId: proofId,
      });
    } catch (error) {
      reportError('Proof file upload failed', { error, errorName: error?.name });
      const isNetworkError = error instanceof ProofUploadNetworkError;
      updateItemStatus(itemId, {
        status: PROOF_ITEM_STATUS.ERROR,
        errorMessage: isNetworkError ? messages.proofUploadNetworkError : messages.proofUploadFailed,
      });
    }
  };

  const startUpload = (itemId, file) => {
    const promise = uploadOne(itemId, file).finally(() => {
      pendingUploadPromisesRef.current.delete(promise);
    });
    pendingUploadPromisesRef.current.add(promise);
  };

  const handleSelectFiles = event => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setTopLevelError(null);

    const newItems = [];
    let firstValidationError = null;

    files.forEach(file => {
      const result = validateFile(file);
      if (!result.valid) {
        if (!firstValidationError) firstValidationError = result.error;
        return;
      }
      newItems.push({
        id: makeLocalId(),
        file,
        originalFileName: file.name,
        mimeType: file.type,
        status: PROOF_ITEM_STATUS.PENDING,
        downloadUrl: null,
        fileProofId: null,
        isPrefilled: false,
      });
    });

    if (firstValidationError) {
      setTopLevelError(errorMessageFor(firstValidationError, messages));
    }
    if (newItems.length) {
      setItems(prev => [...prev, ...newItems]);
      // Upload on select so the partner sees progress and isn't held on submit.
      newItems.forEach(item => startUpload(item.id, item.file));
    }

    // Reset so re-selecting the same file (e.g. after a remove) still fires.
    event.target.value = '';
  };

  const handleRemove = id => {
    setItems(prev => prev.filter(item => item.id !== id));
    setTopLevelError(null);
  };

  const handleRetry = id => {
    const item = itemsRef.current.find(i => i.id === id);
    if (item && item.file) startUpload(id, item.file);
  };

  useImperativeHandle(
    ref,
    () => ({
      openPicker: () => inputRef.current?.click(),

      collectProofs: async () => {
        if (pendingUploadPromisesRef.current.size) {
          await Promise.all(Array.from(pendingUploadPromisesRef.current));
        }

        const { current } = itemsRef;
        const errored = current.filter(item => item.status === PROOF_ITEM_STATUS.ERROR);
        if (errored.length) {
          throw new Error('One or more proof files failed to upload');
        }

        return current
          .filter(item => item.status === PROOF_ITEM_STATUS.SUCCESS && item.fileProofId)
          .map(item => ({
            type: RetailRateRequestProofType.RETAIL_RATE_REQUEST_PROOF_TYPE_FILE,
            file_proof_id: item.fileProofId,
            uploaded_by: String(userId),
          }));
      },

      hasFiles: () => itemsRef.current.length > 0,
    }),
    [userId]
  );

  return (
    <div className={block()}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        multiple
        onChange={handleSelectFiles}
        className={element('hidden-input')}
        data-testid="proof-file-upload-input"
      />

      {items.length > 0 && (
        <div className={element('list')} data-testid="proof-file-upload-list">
          {items.map(item => (
            <ProofFileUploadItem key={item.id} item={item} onRemove={handleRemove} onRetry={handleRetry} />
          ))}
        </div>
      )}

      {topLevelError && (
        <Message as="p" message={topLevelError} className={element('error')} data-testid="proof-file-upload-error" />
      )}
    </div>
  );
});

ProofFileUpload.displayName = 'ProofFileUpload';

ProofFileUpload.propTypes = {
  venueId: PropTypes.number.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  prefillEnabled: PropTypes.bool,
};

ProofFileUpload.defaultProps = {
  prefillEnabled: true,
};

export default ProofFileUpload;
